import React from 'react';
import styled from 'styled-components';
import moment from 'moment';
import { Link } from 'react-router-dom';
//
import DealListTable from 'components/deal-list/deal-list-table';
import ErrorInline from 'components/errors/inline';
import routes from 'router/routes';
import { mapNameToDisplayName } from 'lib/helpers/field-options';
import { removeUserGroup } from 'lib/helpers/name-formatters';
import { IDeal } from 'store/deals/types';
import { IFieldOption, IStatusFieldOption } from 'store/field-options/types';

// Special date used to conditionally display blank strings in modifiedOn column
const futureDateRaw = '01-01-2099';
const futureDateTime = new Date(futureDateRaw).getTime();

const StyledDealList = styled.div({
  marginTop: 16,
  '.header': {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
});

const StyledLink = styled(Link)`
  font-weight: 600;
`;

export interface IDealList {
  loading: boolean;
  error: Error | undefined | null;
  filteredDeals: IDeal[];
  regions: IFieldOption[] | undefined;
  productLines: IFieldOption[] | undefined;
  statuses: IStatusFieldOption[] | undefined;
}

function renderStyledLinkComponent(id: string | undefined, name: string, row: { original: IDeal }) {
  return <StyledLink to={`${routes.deals}/${id}`}>{name}</StyledLink>;
}

const DealList = ({ loading, error, filteredDeals, regions, productLines, statuses }: IDealList) => {
  // Dynamically map displayName values from API response
  function constructColumns() {
    return [
      {
        accessor: (d: { name: string }) => d.name.toLowerCase(),
        Header: 'Name',
        Cell: ({ row }: { row: { original: IDeal } }) => {
          const { id, name } = row.original;
          return renderStyledLinkComponent(id, name, row);
        },
      },
      {
        accessor: (d: { customer: string }) => d.customer.toLowerCase(),
        Header: 'Customer',
        Cell: ({ row }: { row: { original: IDeal } }) => row.original.customer,
      },
      {
        accessor: 'region',
        Header: 'Region',
        Cell: ({ cell }: { cell: { value: string } }) => mapNameToDisplayName(cell.value, regions),
      },
      {
        accessor: 'productLine',
        Header: 'Product Line',
        Cell: ({ cell }: { cell: { value: string } }) => mapNameToDisplayName(cell.value, productLines),
      },
      // Margin At Risk($) will be required once implementation is done from backend to suupport here.
      // {
      //   accessor: 'margin_at_risk',
      //   Header: 'Margin At Risk($)',
      //   Cell: ({ row }: any) =>
      //     row.original.margin_at_risk && row.original.volume
      //       ? formatNumber(row.original.margin_at_risk * row.original.volume)
      //       : '',
      // },
      {
        accessor: 'status',
        Header: 'Status',
        Cell: ({ cell }: { cell: { value: string } }) => mapNameToDisplayName(cell.value, statuses),
      },
      {
        accessor: 'createdBy',
        Header: 'Created By',
        Cell: ({ cell }: { cell: { value: string } }) => removeUserGroup(cell.value),
      },
      {
        id: 'createdOn',
        accessor: ({ createdOn }: IDeal) => new Date(createdOn ?? futureDateRaw),
        Header: 'Created On',
        sortType: 'datetime',
        Cell: ({ cell }: { cell: { value: Date } }) => {
          const { value } = cell;
          return value.getTime() === futureDateTime ? null : moment(value).format('MM/DD/YYYY');
        },
      },
      {
        accessor: 'modifiedBy',
        Header: 'Modified By',
        Cell: ({ cell }: { cell: { value: string } }) => removeUserGroup(cell.value),
      },
      {
        accessor: ({ modifiedOn }: IDeal) => new Date(modifiedOn ?? futureDateRaw),
        Header: 'Modified On',
        sortType: 'datetime',
        Cell: ({ cell }: { cell: { value: Date } }) => {
          const { value } = cell;
          return value.getTime() === futureDateTime ? null : moment(value).format('MM/DD/YYYY');
        },
      },
    ];
  }

  function renderContent() {
    if (loading) {
      return null;
    }

    if (filteredDeals) {
      return <DealListTable columns={constructColumns()} data={filteredDeals} />;
    }

    if (error) {
      return <ErrorInline error={error} />;
    }

    return <p>There was an error loading the page.</p>;
  }

  return <StyledDealList>{renderContent()}</StyledDealList>;
};

export default DealList;
